import { Button, ContentType, getDeviceLocale, reportError } from "@cabocheck/shared";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { data, redirect } from "react-router";
import { Form, useActionData, useLoaderData, useNavigation } from "react-router";
import { useTranslation } from "react-i18next";
import { client } from "~/client/client";
import { baseParams } from "~/client/baseParams";
import { getDeviceGuidOrGenerate } from "~/utils/getDeviceGuidOrGenerate";
import { commitSession, getSession } from "../sessions.server";
import { emailLoginHeaders, privateLoginFormHeaders, requireLoginOrigin } from "~/utils/emailLogin.server";

export const headers = emailLoginHeaders;

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await getSession(request.headers.get("Cookie"));

  const r = { error: session.get("error") };

  return data(r, {
    headers: {
      ...privateLoginFormHeaders,
      "Set-Cookie": await commitSession(session),
    },
  });
}

export default function ClientPage() {
  const { t } = useTranslation();
  const { error } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();

  const navigation = useNavigation();
  const loading = Boolean(navigation.state === "submitting");

  return (
    <div className="flex justify-center">
      <Form method="post">
        <div className="flex flex-col items-stretch justify-center gap-4">
          {error ? <div className="text-center text-red-500">{error}</div> : null}
          {actionData ? (
            <div role="alert" className="text-red-500">
              {t(actionData.message)}
            </div>
          ) : null}
          <input
            type="email"
            required
            name="email"
            className="w-72 rounded-md border p-3 sm:w-96"
            placeholder="Email Adresse"
            autoFocus
            autoComplete="username"
          />
          <Button type="submit" disabled={loading}>
            {t("Login")}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export async function action({ request }: ActionFunctionArgs) {
  requireLoginOrigin(request);
  const session = await getSession(request.headers.get("Cookie"));
  const body = await request.formData();
  const email = body.get("email") as string | null;
  if (!email) {
    return data({ message: "Email is required" });
  }

  const device_guid = await getDeviceGuidOrGenerate(request);

  session.set("device_guid", device_guid);

  let registration_token: string | undefined;

  try {
    const { data: r, error } = await client.v1.postAuthPreRegLaunch(
      {
        ...baseParams,
        device_guid: device_guid,
        device_locale: getDeviceLocale(request),
      },
      { format: "json", type: ContentType.Json },
    );

    if (error) {
      reportError("Login initiation failed", error);
      session.flash("error", error.error);
      return data({ message: "Something went wrong" }, { headers: { "Set-Cookie": await commitSession(session) } });
    }

    registration_token = r.response?.registration_token;
    if (!registration_token) {
      session.flash("error", "registration_token is missing");
      return data({ message: "Something went wrong" }, { headers: { "Set-Cookie": await commitSession(session) } });
    }

    session.set("registration_token", registration_token);
  } catch (error) {
    reportError("Login initiation failed", error);
    return data({ message: "Something went wrong" }, { headers: { "Set-Cookie": await commitSession(session) } });
  }

  return redirect(`/v1/auth/login/password?email=${encodeURIComponent(email)}`, {
    headers: { "Set-Cookie": await commitSession(session) },
  });
}
